import { Grid, Typography } from '@mui/material';
import DocumentMarriageForm from 'civil-status/components/document-marriage-form';
import ButtonsForm from 'civil-status/components/shared/buttons';
import { DocumentSearchMarriageCrumbs } from 'civil-status/constants/breadcrumb';
import createSchema from 'civil-status/utils/create-schema-marriage';
import {
  transformDocumentMarriage,
  transformDocumentMarriageRequestUpdate,
} from 'civil-status/utils/transform-document-marriage';
import { DocumentMarriageFormVM } from 'civil-status/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentMarriageCM } from 'shell/models/document-marriage';
import { HttpResponse } from 'shell/models/http-response';
import { toast } from 'react-toastify';
import DescriptionForm from 'civil-status/components/shared/description-document';
import { useUserProfile } from 'shell/hooks/use-user-profile';

const typeAct = 'M';
export default function DocumentMarriageFormPage(): React.ReactElement {
  const params = useParams();

  const { userProfile } = useUserProfile();

  const [marriage] = useHttpFetch<HttpResponse<DocumentMarriageCM>, DocumentMarriageFormVM>(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/visualizzazione-atto-matrimonio/${params.anno}-${params.numeroRegistro}-${params.numeroAtto}-${params.parte}-${params.serie1}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: transformDocumentMarriage,
    }
  );

  const updateMarriage = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/modifica-atto-matrimonio`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const substituteImage = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/sostituzione-immagine-atto-matrimonio`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [mode, setMode] = useState<PageMode>(PageMode.Detail);

  const validationSchema = useMemo(() => {
    return createSchema(mode);
  }, [mode]);

  const formikForm = useFormik<DocumentMarriageFormVM>({
    initialValues: (marriage.data as DocumentMarriageFormVM) || {
      annotationNational: '',
      noteNational: '',
      annotationInternational: '',
      noteInternational: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, formContext) => {
      // If act substitution mode
      if (mode === PageMode.Substitution) {
        (
          substituteImage({
            data: {
              // TODO MAPPING, to test
              attoMatrimonioForUpload: {
                numeroRegistro: values?.registerNumber || undefined,
                anno: values?.year || undefined,
                numeroAtto: values?.documentNumber || undefined,
                parte: values?.portion || undefined,
                serie: `${values?.serie || ' '}${values?.serie2 || ''}` || undefined,
                matricola1: values?.matricolaHusband || undefined,
                cognome1: values?.surnameHusband || undefined,
                nome1: values?.nameHusband || undefined,
                matricola2: values?.matricolaWife || undefined,
                cognome2: values?.surnameWife || undefined,
                nome2: values?.nameWife || undefined,
                idOperatore: userProfile.datiLogin?.key?.userName || undefined,
              },
              filename: values.uploadFile?.file?.name || undefined,
              mimeType: values.uploadFile?.file?.type || undefined,
              file: values.uploadFile?.rawTiff?.split(',')[1] || undefined,
            },
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          formContext.setFieldValue(
            'indicatorUpdate',
            'È necessario effettuare la convalida dei dati.'
          );
        });
      }

      // If edit mode
      if (mode === PageMode.Edit) {
        const data = transformDocumentMarriageRequestUpdate(values);
        (
          updateMarriage({
            data,
          }) as ResponsePromise<void>
        ).then(() => {
          formContext.setFieldValue('indicatorUpdate', '');
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
        });
      }
    },
  });

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  useEffect(() => {
    return () => {
      if (params) {
        unlockDocument({
          data: {
            key: {
              numeroAtto: params.numeroAtto,
              numeroRegistro: params.numeroRegistro,
              parte: params.parte,
              serie1: params.serie1,
              anno: params.anno,
            },
          },
        });
      }
    };
  }, []);

  // visibility
  const [visibility, setVisibility] = useState(false);

  useEffect(() => {
    setVisibility(!!formikForm.values?.indicatorUpdate);
  }, [formikForm.values?.indicatorUpdate]);

  const deleteAll = useHttpClient(`${__CIVIL_STATUS_API_URL__}/annotazioni-atto/rimuovi/all`, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const handleDeleteAllNational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: typeAct,
            numeroRegistro: formikForm.values?.registerNumber,
            anno: formikForm.values?.year,
            numeroAtto: formikForm.values?.documentNumber,
            parte: formikForm.values?.portion,
            serie1: formikForm.values?.serie,
          },
          tipoCertificato: 'NAZ',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      formikForm.setFieldValue('annotationNationalList', []);
      setMode(PageMode.Detail); // deatil mode
    });
  }, [formikForm.values, typeAct, deleteAll, setMode]);

  const handleDeleteAllInternational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: typeAct,
            numeroRegistro: formikForm.values?.registerNumber,
            anno: formikForm.values?.year,
            numeroAtto: formikForm.values?.documentNumber,
            parte: formikForm.values?.portion,
            serie1: formikForm.values?.serie,
          },
          tipoCertificato: 'INT',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      formikForm.setFieldValue('annotationInternationalList', []);
      setMode(PageMode.Detail); // deatil mode
    });
  }, [deleteAll, setMode, formikForm.values, typeAct]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onChanges = useCallback(
    (newValues) => {
      formikForm.setValues({
        ...formikForm.values,
        ...newValues,
      });
    },
    [formikForm.setValues, formikForm.values]
  );

  return (
    <>
      <Breadcrumbs
        items={[
          ...DocumentSearchMarriageCrumbs,
          { name: 'Atto di Matrimonio', path: '/stato-civile/matrimonio/ricerca/dettaglio' },
        ]}
        base="stato-civile"
      />
      <form onSubmit={formikForm.handleSubmit}>
        <Grid container spacing={2}>
          <Grid container spacing={2} item xs={12} md={4}>
            <Grid item xs={12} md={6}>
              <Typography
                color="secondary.main"
                style={{
                  fontSize: '26px',
                }}
              >
                Atto di Matrimonio{' '}
                {mode === PageMode.International ? '- Annotazioni Internazionali' : ''}
                {mode === PageMode.National ? '- Annotazioni Italia' : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <DescriptionForm values={formikForm.values} />
            </Grid>
          </Grid>
          <Grid item xs={12} md={8}>
            <ButtonsForm
              mode={mode}
              onAnnotationInternationalMode={() => {
                setMode(PageMode.International);
              }}
              onAnnotationNationalMode={() => {
                setMode(PageMode.National);
              }}
              onDetailMode={() => {
                setMode(PageMode.Detail);
              }}
              onEditMode={() => {
                setMode(PageMode.Edit);
              }}
              onPrintMode={() => {
                setMode(PageMode.Print);
              }}
              onSubstitutionMode={() => {
                setMode(PageMode.Substitution);
              }}
              onAnnotationInternationalDeleteAll={handleDeleteAllInternational}
              onAnnotationNationalDeleteAll={handleDeleteAllNational}
              url="/stato-civile/matrimonio/ricerca"
            />
          </Grid>
        </Grid>
        <SearchMessages
          type={MessageType.Warning}
          title={formikForm.values?.indicatorUpdate ? `C'è 1 warning` : ''}
          messages={
            formikForm.values?.indicatorUpdate
              ? [{ id: 1, value: formikForm.values?.indicatorUpdate }]
              : []
          }
          visibility={visibility}
          onChangeVisibility={() => setVisibility(false)}
        />
        <DocumentMarriageForm
          onChange={formikForm.setFieldValue}
          onChanges={onChanges}
          values={formikForm.values}
          errors={formikForm.errors}
          touched={formikForm.touched}
          mode={mode}
          type={typeAct}
          setMode={setMode}
        />
      </form>
    </>
  );
}

DocumentMarriageFormPage.displayName = 'home page component';
